/**
 * @file queryClient.ts
 * @brief Singleton TanStack Query client used across the app.
 *
 * @details
 * - Reasonable defaults: 1 retry on failed queries, 30s staleTime,
 *   no refetch on window focus (admin panels usually don't need it).
 * - 401 errors are NOT retried — they always represent a real auth
 *   failure, and the response interceptor in `client.ts` will already
 *   have cleared the token by the time the retry would run.
 *
 * @dependencies
 * - @tanstack/react-query
 * - shared/api/client (ApiClientError)
 *
 * @author DES
 * @date 26-05-2026
 */

import { QueryClient } from "@tanstack/react-query";
import { ApiClientError } from "./client";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiClientError && error.status === 401) {
          return false;
        }
        // B39: older than the history window; asking again cannot help.
        if (error instanceof ApiClientError && error.code === "HISTORY_LOCKED") {
          return false;
        }
        return failureCount < 1;
      },
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});
